import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import SafeIcon from '../common/SafeIcon';
import { FiMail, FiRefreshCw, FiCheck, FiSend, FiAlertCircle, FiLoader, FiSettings, FiExternalLink, FiTrash2, FiPlay, FiZap } from 'react-icons/fi';
import { sendEmail, testMailgunConnection, sendBulkEmails } from '../utils/emailService';
import EmailConfigAdmin from './EmailConfigAdmin';

const EmailAdmin = () => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [autoSendActive, setAutoSendActive] = useState(false);
  const [sendingStatus, setSendingStatus] = useState(null);
  const [testingMailgun, setTestingMailgun] = useState(false);

  // Fetch pending emails on component mount
  useEffect(() => {
    fetchPendingEmails();
    // Check if auto-send was previously enabled
    const wasAutoSendActive = localStorage.getItem('autoSendActive') === 'true';
    if (wasAutoSendActive) {
      setAutoSendActive(true);
    }
  }, []);

  // Auto-send timer effect
  useEffect(() => {
    let intervalId = null;
    if (autoSendActive) {
      // Save state to localStorage
      localStorage.setItem('autoSendActive', 'true');
      // Initial processing
      processEmailsAutomatically();
      // Set up interval (every 30 seconds for Mailgun)
      intervalId = setInterval(() => {
        processEmailsAutomatically();
      }, 30000); // 30 seconds
      setSendingStatus('🚀 Mailgun Auto-Versand aktiv! E-Mails werden alle 30 Sekunden versendet.');
    } else {
      // Clear localStorage
      localStorage.setItem('autoSendActive', 'false');
      setSendingStatus(null);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoSendActive]);

  const fetchPendingEmails = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('email_queue_hb47x')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        console.error('Error fetching emails:', error);
        return;
      }

      setEmails(data || []);
    } catch (error) {
      console.error('Failed to fetch emails:', error);
    } finally {
      setLoading(false);
    }
  };

  // Automatic processing function (MAILGUN API!)
  const processEmailsAutomatically = async () => {
    try {
      // Get pending emails without updating UI state
      const { data: pendingEmails, error: emailError } = await supabase
        .from('email_queue_hb47x')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: true })
        .limit(10); // Mehr E-Mails pro Batch da Mailgun zuverlässiger ist

      if (emailError || !pendingEmails || pendingEmails.length === 0) {
        return;
      }

      console.log(`🚀 Mailgun Auto-processing ${pendingEmails.length} E-Mails...`);
      setSendingStatus(`🚀 Versende automatisch ${pendingEmails.length} E-Mails via Mailgun...`);

      // Use bulk email function for better performance
      const emailsToSend = pendingEmails.map(email => ({
        to: email.recipient,
        subject: email.subject,
        html: email.html_content,
        emailType: email.email_type
      }));

      const bulkResult = await sendBulkEmails(emailsToSend);

      // Refresh the email list
      const { data } = await supabase
        .from('email_queue_hb47x')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (data) {
        setEmails(data);
      }

      setSendingStatus(`✅ Mailgun Auto-Versand: ${bulkResult.success} E-Mails gesendet, ${bulkResult.error} fehlgeschlagen. Prüft alle 30 Sekunden.`);
    } catch (error) {
      console.error('Fehler beim automatischen E-Mail-Versand:', error);
      setSendingStatus(`❌ Fehler beim Mailgun Auto-Versand: ${error.message}`);
    }
  };

  const processEmails = async () => {
    setProcessing(true);
    setResult(null);

    try {
      // Get pending emails
      const { data: pendingEmails, error: emailError } = await supabase
        .from('email_queue_hb47x')
        .select('*')
        .eq('status', 'pending')
        .order('created_at', { ascending: true })
        .limit(20); // Mehr E-Mails da Mailgun schneller ist

      if (emailError) {
        setResult({
          success: false,
          message: `Fehler beim Laden der E-Mails: ${emailError.message}`
        });
        setProcessing(false);
        return;
      }

      if (!pendingEmails || pendingEmails.length === 0) {
        setResult({
          success: true,
          data: { processed: 0, success: 0, error: 0 },
          message: 'Keine ausstehenden E-Mails zum Versenden.'
        });
        setProcessing(false);
        return;
      }

      console.log(`🚀 Versende ${pendingEmails.length} E-Mails via Mailgun...`);

      // Use bulk email function
      const emailsToSend = pendingEmails.map(email => ({
        to: email.recipient,
        subject: email.subject,
        html: email.html_content,
        emailType: email.email_type
      }));

      const bulkResult = await sendBulkEmails(emailsToSend);

      setResult({
        success: true,
        data: {
          processed: bulkResult.total,
          success: bulkResult.success,
          error: bulkResult.error
        },
        message: `✅ ${bulkResult.success} E-Mails via Mailgun erfolgreich versendet, ${bulkResult.error} fehlgeschlagen.`
      });

      // Refresh the email list
      fetchPendingEmails();
    } catch (error) {
      console.error('Fehler beim E-Mail-Versand:', error);
      setResult({
        success: false,
        message: `❌ Fehler: ${error.message}`
      });
    } finally {
      setProcessing(false);
    }
  };

  const testMailgun = async () => {
    setTestingMailgun(true);
    try {
      const result = await testMailgunConnection();
      if (result.success) {
        setResult({
          success: true,
          message: '✅ Mailgun API Test erfolgreich! Test-E-Mail wurde versendet.'
        });
      } else {
        setResult({
          success: false,
          message: `❌ Mailgun API Test fehlgeschlagen: ${result.error}`
        });
      }
    } catch (error) {
      setResult({
        success: false,
        message: `❌ Mailgun Test Fehler: ${error.message}`
      });
    } finally {
      setTestingMailgun(false);
    }
  };

  const markEmailAsSent = async (emailId) => {
    try {
      const { error } = await supabase
        .from('email_queue_hb47x')
        .update({
          status: 'sent',
          sent_at: new Date().toISOString()
        })
        .eq('id', emailId);

      if (error) {
        console.error('Error marking email as sent:', error);
        return false;
      }

      // Refresh the email list
      fetchPendingEmails();
      return true;
    } catch (error) {
      console.error('Failed to mark email as sent:', error);
      return false;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-500';
      case 'sent': return 'text-green-500';
      case 'failed': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  // Handler to open a specific email in the default mail client
  const openEmailInMailClient = (email) => {
    const body = `
E-Mail-Inhalt:
${email.html_content.replace(/<[^>]*>?/gm, '')}
`;
    window.open(`mailto:${email.recipient}?subject=${encodeURIComponent(email.subject)}&body=${encodeURIComponent(body)}`);
  };

  const deleteEmail = async (emailId) => {
    if (!confirm('Sind Sie sicher, dass Sie diese E-Mail löschen möchten?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('email_queue_hb47x')
        .delete()
        .eq('id', emailId);

      if (error) {
        console.error('Error deleting email:', error);
        return;
      }

      // Refresh the email list
      fetchPendingEmails();
    } catch (error) {
      console.error('Failed to delete email:', error);
    }
  };

  const toggleAutoSend = () => {
    setAutoSendActive(!autoSendActive);
  };

  if (showConfig) {
    return (
      <div className="max-w-6xl mx-auto p-4">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">
            E-Mail Konfiguration (Mailgun API)
          </h1>
          <button
            onClick={() => setShowConfig(false)}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            Zurück zur E-Mail-Verwaltung
          </button>
        </div>
        <EmailConfigAdmin />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center">
          <SafeIcon icon={FiMail} className="mr-2 text-orange-500" />
          🚀 Mailgun API E-Mail Verwaltung
        </h1>
        <div className="flex space-x-2">
          <button
            onClick={testMailgun}
            className="px-4 py-2 bg-purple-500 text-white rounded-lg flex items-center hover:bg-purple-600"
            disabled={testingMailgun}
          >
            <SafeIcon icon={testingMailgun ? FiLoader : FiZap} className={`mr-1 ${testingMailgun ? 'animate-spin' : ''}`} />
            {testingMailgun ? 'Teste...' : 'Mailgun Test'}
          </button>
          <button
            onClick={() => setShowConfig(true)}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg flex items-center hover:bg-blue-600"
          >
            <SafeIcon icon={FiSettings} className="mr-1" />
            Mailgun Einstellungen
          </button>
          <button
            onClick={fetchPendingEmails}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg flex items-center hover:bg-gray-200"
            disabled={loading}
          >
            <SafeIcon icon={loading ? FiLoader : FiRefreshCw} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </button>
          <button
            onClick={toggleAutoSend}
            className={`px-4 py-2 ${autoSendActive ? 'bg-green-500' : 'bg-gray-400'} text-white rounded-lg flex items-center hover:${autoSendActive ? 'bg-green-600' : 'bg-gray-500'}`}
          >
            <SafeIcon icon={autoSendActive ? FiCheck : FiPlay} className="mr-1" />
            {autoSendActive ? '🚀 Mailgun Auto-Versand aktiv' : 'Mailgun Auto-Versand starten'}
          </button>
          <button
            onClick={processEmails}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg flex items-center hover:bg-orange-600"
            disabled={processing || emails.filter(e => e.status === 'pending').length === 0}
          >
            <SafeIcon icon={processing ? FiLoader : FiSend} className={`mr-1 ${processing ? 'animate-spin' : ''}`} />
            🚀 Mailgun E-Mails senden ({emails.filter(e => e.status === 'pending').length})
          </button>
        </div>
      </div>

      {result && (
        <div className={`mb-4 p-4 rounded-lg ${result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <div className="flex items-center">
            <SafeIcon icon={result.success ? FiCheck : FiAlertCircle} className={`mr-2 ${result.success ? 'text-green-500' : 'text-red-500'}`} />
            <div>
              {result.success && result.data ? (
                <div>
                  <p className="font-medium">🚀 {result.data.processed} E-Mails via Mailgun verarbeitet</p>
                  <p>✅ {result.data.success} erfolgreich gesendet, ❌ {result.data.error} fehlgeschlagen</p>
                  {result.message && <p className="text-sm mt-1 font-bold">{result.message}</p>}
                </div>
              ) : (
                <p className="font-medium">{result.message}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Auto-send status */}
      {sendingStatus && (
        <div className="mb-4 p-4 rounded-lg bg-green-50 border border-green-200">
          <div className="flex items-center">
            <SafeIcon icon={FiPlay} className="mr-2 text-green-500" />
            <p className="font-medium text-green-800">{sendingStatus}</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Empfänger
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Betreff
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Typ
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Erstellt
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aktionen
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center">
                    <SafeIcon icon={FiLoader} className="animate-spin mx-auto text-orange-500 text-xl" />
                    <p className="mt-2 text-gray-500">E-Mails werden geladen...</p>
                  </td>
                </tr>
              ) : emails.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                    Keine E-Mails in der Warteschlange
                  </td>
                </tr>
              ) : (
                emails.map((email) => (
                  <tr key={email.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {email.recipient}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {email.subject}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {email.email_type === 'order_notification' ? 'Bestellbenachrichtigung' :
                       email.email_type === 'customer_confirmation' ? 'Kundenbestätigung' : 'Andere'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(email.status)}`}>
                        {email.status === 'pending' ? '⏳ Ausstehend' :
                         email.status === 'sent' ? '✅ Gesendet' :
                         email.status === 'failed' ? '❌ Fehlgeschlagen' : email.status}
                        {email.retry_count > 0 && email.status === 'failed' && ` (${email.retry_count})`}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(email.created_at).toLocaleString('de-DE')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex space-x-2">
                        {email.status === 'pending' && (
                          <>
                            <button
                              onClick={() => markEmailAsSent(email.id)}
                              className="text-green-600 hover:text-green-800"
                              title="Als gesendet markieren"
                            >
                              <SafeIcon icon={FiCheck} />
                            </button>
                            <button
                              onClick={() => openEmailInMailClient(email)}
                              className="text-blue-600 hover:text-blue-800"
                              title="Im E-Mail-Programm öffnen"
                            >
                              <SafeIcon icon={FiExternalLink} />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => deleteEmail(email.id)}
                          className="text-red-600 hover:text-red-800"
                          title="E-Mail löschen"
                        >
                          <SafeIcon icon={FiTrash2} />
                        </button>
                        {email.status === 'failed' && (
                          <div className="text-xs text-red-600 max-w-xs truncate" title={email.error_message}>
                            {email.error_message || 'Unbekannter Fehler'}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 p-4 bg-purple-50 rounded-lg">
        <h3 className="font-semibold text-purple-800 mb-2">🚀 MAILGUN API E-MAIL-VERSAND:</h3>
        <ul className="text-sm text-purple-700 space-y-1">
          <li>• ✅ PROFESSIONELLER E-Mail-Versand über Mailgun API</li>
          <li>• 🔄 Automatische Fallbacks zu EmailJS und Formspree</li>
          <li>• ⏰ Auto-Versand prüft alle 30 Sekunden auf neue E-Mails</li>
          <li>• 📧 Bulk-E-Mail Unterstützung für bessere Performance</li>
          <li>• 💯 Bis zu 10.000 E-Mails pro Monat mit Mailgun</li>
          <li>• 🔐 API-Key: cb926e8151838ea2656b48ac2b6ca6cd-03fd4b1a-b7b4fa36</li>
        </ul>
      </div>
    </div>
  );
};

export default EmailAdmin;